import { useEffect, useState, type ReactNode } from 'react';
import { IconButton } from './IconButton';
import { classNames } from '~/utils/classNames';

interface PaginationProps {
  className?: string;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  pageSize?: number;
  totalItems?: number;
  descriptionNode?: ReactNode;
  allowPageInput?: boolean;
}

export const Pagination = ({
  className,
  page,
  totalPages,
  onPageChange,
  descriptionNode,
  pageSize,
  totalItems,
  allowPageInput = false,
}: PaginationProps) => {
  const [inputPage, setInputPage] = useState<number | string>(page);
  useEffect(() => {
    setInputPage(page);
  }, [page]);

  const tryChangePage = () => {
    if (typeof inputPage === 'number' || !isNaN(parseInt(inputPage))) {
      onPageChange(typeof inputPage === 'number' ? inputPage : parseInt(inputPage));
    }
  };

  return (
    <div
      className={classNames(
        'flex items-center gap-2 w-full justify-between border border-bolt-ds-border-divider rounded-lg p-2',
        className,
      )}
    >
      {descriptionNode && (
        <span className="text-sm text-bolt-ds-textSecondary w-full text-nowrap px-2">{descriptionNode}</span>
      )}
      {pageSize && totalItems && (
        <span className="text-sm text-bolt-ds-textSecondary w-full text-nowrap px-2">
          <span className="text-bolt-ds-textPrimary font-medium">
            {pageSize * (page - 1) + 1}-{Math.min(pageSize * page, totalItems)}
          </span>{' '}
          of <span className="text-bolt-ds-textPrimary font-medium">{totalItems}</span>
        </span>
      )}
      <div className={classNames('flex items-center gap-2 flex-1', { 'justify-between': !descriptionNode })}>
        <IconButton
          size="sm"
          variant="ghost"
          disabled={page === 1}
          onClick={() => onPageChange(page - 1)}
          className="transition-colors"
          icon="i-heroicons:arrow-left-solid"
        />
        <span className="min-w-15 px-1 flex items-center justify-center gap-1 text-sm text-bolt-ds-textSecondary text-nowrap">
          Page{' '}
          {allowPageInput ? (
            <input
              value={inputPage}
              className="mx-1 w-10 px-1.5 py-1 bg-bolt-ds-surfaceOne border rounded-md focus:ring-2 outline-none border-bolt-ds-borderOutline focus:ring-bolt-ds-brandHighlight"
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  tryChangePage();
                }
              }}
              onBlur={tryChangePage}
              onChange={(event) => {
                const value = event.target.value;

                if (!isNaN(parseInt(value))) {
                  setInputPage(parseInt(value));
                } else if (value === '') {
                  setInputPage('');
                }
              }}
            />
          ) : (
            page
          )}{' '}
          of {totalPages}
        </span>
        <IconButton
          size="sm"
          variant="ghost"
          disabled={page === totalPages}
          onClick={() => onPageChange(page + 1)}
          className="transition-colors"
          icon="i-heroicons:arrow-right-solid"
        />
      </div>
    </div>
  );
};
